import type { EnvelopeStyle, Mood } from '@just-us/shared'
/**
 * pages/note-compose.tsx
 *
 * Note compose screen.
 * - Textarea with Caveat font
 * - Mood chip row (lovey / celebratory / dreamy / cozy)
 * - Envelope style picker (plain / heart / flower)
 * - Doodle canvas toggle (bottom sheet)
 * - Time-lock toggle with datetime input
 * - Send button → navigates to /notes on success
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DoodleCanvas from '../components/doodle-canvas'
import { useCreateNote } from '../hooks/use-notes'
import { useUploadDoodle } from '../hooks/use-notes'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MOODS: { value: Mood; label: string; emoji: string }[] = [
  { value: 'lovey', label: 'Lovey', emoji: '💕' },
  { value: 'celebratory', label: 'Celebratory', emoji: '🎉' },
  { value: 'dreamy', label: 'Dreamy', emoji: '🌙' },
  { value: 'cozy', label: 'Cozy', emoji: '☕' },
]

const ENVELOPES: { value: EnvelopeStyle; emoji: string; label: string }[] = [
  { value: 'plain', emoji: '✉️', label: 'Plain' },
  { value: 'heart', emoji: '💌', label: 'Heart' },
  { value: 'flower', emoji: '🌸', label: 'Flower' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a local datetime-local string to a Unix timestamp. */
const localDateTimeToUnix = (val: string): number => Math.floor(new Date(val).getTime() / 1000)

/** Minimum datetime-local value = now (rounded down to the minute) */
const minDateTime = (): string => {
  const d = new Date()
  d.setSeconds(0, 0)
  return d.toISOString().slice(0, 16)
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function NoteComposePage() {
  const navigate = useNavigate()
  const createNote = useCreateNote()
  const uploadDoodle = useUploadDoodle()

  const [content, setContent] = useState('')
  const [mood, setMood] = useState<Mood | undefined>('lovey')
  const [envelopeStyle, setEnvelopeStyle] = useState<EnvelopeStyle>('plain')
  const [timeLockOn, setTimeLockOn] = useState(false)
  const [unlockAt, setUnlockAt] = useState<string>(minDateTime())
  const [showDoodle, setShowDoodle] = useState(false)
  const [doodleBlob, setDoodleBlob] = useState<Blob | null>(null)
  const [doodlePreviewUrl, setDoodlePreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const canSend = content.trim().length > 0 || doodleBlob !== null
  const isSending = createNote.isPending || uploadDoodle.isPending

  const handleDoodleDone = (blob: Blob) => {
    setDoodleBlob(blob)
    setDoodlePreviewUrl(URL.createObjectURL(blob))
    setShowDoodle(false)
  }

  const handleRemoveDoodle = () => {
    if (doodlePreviewUrl) URL.revokeObjectURL(doodlePreviewUrl)
    setDoodleBlob(null)
    setDoodlePreviewUrl(null)
  }

  const handleSend = async () => {
    if (!canSend || isSending) return
    setError(null)

    try {
      let doodle_encrypted_key: string | undefined

      if (doodleBlob) {
        const result = await uploadDoodle.mutateAsync(doodleBlob)
        doodle_encrypted_key = result.encrypted_key
      }

      await createNote.mutateAsync({
        content: content.trim() || undefined,
        mood,
        envelope_style: envelopeStyle,
        unlock_at: timeLockOn ? localDateTimeToUnix(unlockAt) : undefined,
        doodle_encrypted_key,
      })

      navigate('/notes')
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            New Note
          </span>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || isSending}
            className="h-[36px] px-5 rounded-full bg-[#D4537E] font-['DM_Sans'] text-[14px] font-medium text-white shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-[0.97] active:bg-[#993556] transition-all duration-150 disabled:opacity-40 disabled:shadow-none"
          >
            {isSending ? '...' : 'Send'}
          </button>
        </div>
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto px-5 pb-[40px] pt-4 flex flex-col gap-5">
        {/* Error banner */}
        {error && (
          <div className="rounded-[12px] bg-red-50 border border-red-200 px-4 py-3">
            <p className="font-['DM_Sans'] text-[13px] text-[#C0392B]">{error}</p>
          </div>
        )}

        {/* Note textarea */}
        <div className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write something sweet..."
            maxLength={2000}
            rows={7}
            className="w-full resize-none bg-transparent outline-none font-['Caveat'] text-[21px] leading-[1.7] text-[#2C1810] placeholder:text-[#B89AA2]"
          />
          <div className="flex justify-end mt-1">
            <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
              {content.length}/2000
            </span>
          </div>
        </div>

        {/* Doodle preview or add button */}
        {doodlePreviewUrl ? (
          <div className="relative rounded-[16px] overflow-hidden border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)]">
            <img
              src={doodlePreviewUrl}
              alt="Your doodle"
              className="w-full object-contain bg-[#FAF8F4]"
            />
            <button
              type="button"
              onClick={handleRemoveDoodle}
              className="absolute top-2 right-2 w-[28px] h-[28px] rounded-full bg-white border border-[#F4C0D1] flex items-center justify-center text-[14px] active:scale-90 transition-all duration-150 shadow-[0_2px_8px_rgba(212,83,126,0.1)]"
              aria-label="Remove doodle"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDoodle(true)}
            className="flex items-center gap-2 h-[44px] px-4 rounded-[12px] border border-[#F4C0D1] bg-white font-['DM_Sans'] text-[14px] font-medium text-[#D4537E] active:scale-[0.97] active:bg-[#FBEAF0] transition-all duration-150 self-start"
          >
            <span>🎨</span>
            <span>Add doodle</span>
          </button>
        )}

        {/* Mood chips */}
        <div>
          <p className="font-['DM_Sans'] text-[12px] font-medium text-[#B89AA2] uppercase tracking-wide mb-2">
            Mood
          </p>
          <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
            {MOODS.map((m) => {
              const active = mood === m.value
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(active ? undefined : m.value)}
                  className="flex items-center gap-[6px] h-[34px] px-3 rounded-full border transition-all duration-150 shrink-0 active:scale-95 font-['DM_Sans'] text-[13px] font-medium"
                  style={{
                    borderColor: active ? '#D4537E' : '#F4C0D1',
                    background: active ? '#FBEAF0' : '#FFFFFF',
                    color: active ? '#D4537E' : '#7A5A62',
                  }}
                >
                  <span>{m.emoji}</span>
                  <span>{m.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Envelope style */}
        <div>
          <p className="font-['DM_Sans'] text-[12px] font-medium text-[#B89AA2] uppercase tracking-wide mb-2">
            Envelope
          </p>
          <div className="flex gap-2">
            {ENVELOPES.map((env) => {
              const active = envelopeStyle === env.value
              return (
                <button
                  key={env.value}
                  type="button"
                  onClick={() => setEnvelopeStyle(env.value)}
                  className="flex flex-col items-center gap-1 w-[72px] h-[60px] rounded-[12px] border transition-all duration-150 active:scale-95"
                  style={{
                    borderColor: active ? '#D4537E' : '#F4C0D1',
                    background: active ? '#FBEAF0' : '#FFFFFF',
                  }}
                >
                  <span className="text-[22px] mt-2">{env.emoji}</span>
                  <span
                    className="font-['DM_Sans'] text-[11px]"
                    style={{ color: active ? '#D4537E' : '#B89AA2' }}
                  >
                    {env.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Time lock */}
        <div className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-['DM_Sans'] text-[14px] font-medium text-[#2C1810]">
                Time lock 🔒
              </p>
              <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">
                Partner opens it only after…
              </p>
            </div>
            {/* Toggle */}
            <button
              type="button"
              role="switch"
              aria-checked={timeLockOn}
              onClick={() => setTimeLockOn((v) => !v)}
              className="relative w-[44px] h-[26px] rounded-full transition-all duration-200 active:scale-95"
              style={{ background: timeLockOn ? '#D4537E' : '#F4C0D1' }}
            >
              <span
                className="absolute top-[3px] w-[20px] h-[20px] rounded-full bg-white shadow transition-all duration-200"
                style={{ left: timeLockOn ? '21px' : '3px' }}
              />
            </button>
          </div>

          {timeLockOn && (
            <div className="mt-3 pt-3 border-t border-[#F9E0E8]">
              <input
                type="datetime-local"
                value={unlockAt}
                min={minDateTime()}
                onChange={(e) => setUnlockAt(e.target.value)}
                className="w-full bg-[#FAF8F4] rounded-[10px] border border-[#F4C0D1] px-3 py-2 font-['DM_Sans'] text-[14px] text-[#2C1810] outline-none focus:border-[#D4537E] transition-colors duration-150"
              />
            </div>
          )}
        </div>
      </main>

      {/* Doodle bottom sheet */}
      {showDoodle && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-50 animate-fade-in"
            onClick={() => setShowDoodle(false)}
          />
          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 max-w-[430px] mx-auto bg-[#FAF8F4] rounded-t-[24px] px-5 pt-4 pb-[env(safe-area-inset-bottom)] z-50 animate-slide-up shadow-[0_-8px_32px_rgba(212,83,126,0.12)]">
            <div className="w-[40px] h-[4px] rounded-full bg-[#F4C0D1] mx-auto mb-4" />
            <p className="font-['Playfair_Display'] text-[17px] font-medium text-[#2C1810] mb-3">
              Draw something
            </p>
            <DoodleCanvas onDone={handleDoodleDone} onCancel={() => setShowDoodle(false)} />
            <div className="h-4" />
          </div>
        </>
      )}
    </div>
  )
}
